import type { AppThunk } from '../../../store';
import { isMultiOrgFeatureEnabled } from '../../../utils/features';
import { selectActiveTenantId } from '.';
import { snapshotGlobalTokensToTenant } from './tenantTokens';

const ADD_ORG_PREVIOUS_TENANT_KEY = 'cosafe.addOrg.previousActiveTenantId';

export function rememberPreAddOrgTenantId(tenantId: number): void {
  try {
    window.sessionStorage.setItem(ADD_ORG_PREVIOUS_TENANT_KEY, String(tenantId));
  } catch {
    // sessionStorage may be unavailable in private mode.
  }
}

export function getPreAddOrgTenantId(): number | null {
  try {
    const raw = window.sessionStorage.getItem(ADD_ORG_PREVIOUS_TENANT_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

export function clearPreAddOrgTenantId(): void {
  try {
    window.sessionStorage.removeItem(ADD_ORG_PREVIOUS_TENANT_KEY);
  } catch {
    /* sessionStorage unavailable — ignore. */
  }
}

/**
 * Begins the add-organisation login flow. Snapshots the current session to
 * per-tenant storage so it survives the ULF exchange that will overwrite the
 * global token slots. Globals are intentionally kept intact so the user
 * remains logged in while the ULF redirect is in progress.
 */
export const beginAddOrganizationFlow = (): AppThunk<void> => (_dispatch, getState) => {
  if (!isMultiOrgFeatureEnabled()) return;

  const activeTenantId = selectActiveTenantId(getState());
  if (activeTenantId != null) {
    rememberPreAddOrgTenantId(activeTenantId);
    // Ensure the current session tokens are saved per-tenant before globals are
    // overwritten by the exchange (e.g. if token rotation advanced globals beyond
    // what was last written to per-tenant storage).
    snapshotGlobalTokensToTenant(activeTenantId);
  } else {
    clearPreAddOrgTenantId();
  }
};
